import { findCountryName } from "@redotech/locale/countries";
import { supplierIdFormat } from "@redotech/redo-inventory-id";
import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const LocationForMoveSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  movable: z.boolean().nullish(),
  message: z.string().nullish(),
  unavailableLineItemsCount: z.number().nullish(),
  selectedByFO: z.boolean().nullish(),
  redoOnly: z.boolean().nullish(),
});
export const LocationForMoveSchema: z.ZodType<LocationForMove> =
  LocationForMoveSchema_;
export interface LocationForMove extends z.infer<
  typeof LocationForMoveSchema_
> {}

const ShopifyLocationAddressSchema = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
  phone: z.string().nullish(),
  province: z.string().nullish(),
  provinceCode: z.string().nullish(),
  zip: z.string().nullish(),
  company: z.string().nullish(),
});

const ShopifyFulfillmentServiceSchema_ = z.object({
  id: z.string(),
  type: z.string().nullish(),
  serviceName: z.string().nullish(),
});
export const ShopifyFulfillmentServiceSchema: z.ZodType<ShopifyFulfillmentService> =
  ShopifyFulfillmentServiceSchema_;
export interface ShopifyFulfillmentService extends z.infer<
  typeof ShopifyFulfillmentServiceSchema_
> {}

const ShopifyLocationSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  address: ShopifyLocationAddressSchema,
  addressVerified: z.boolean(),
  createdAt: z.string(),
  fulfillmentService: ShopifyFulfillmentServiceSchema_.nullish(),
  fulfillsOnlineOrders: z.boolean(),
  isActive: z.boolean(),
  shipsInventory: z.boolean(),
  updatedAt: z.string(),
});
export const ShopifyLocationSchema: z.ZodType<ShopifyFulfillmentLocation> =
  ShopifyLocationSchema_;

export interface ShopifyFulfillmentLocation extends z.infer<
  typeof ShopifyLocationSchema_
> {}

const RedoFulfillmentLocationSchema_ = ShopifyLocationSchema_.omit({
  id: true,
}).extend({
  _id: zExt.objectId().nullish(),
  provider: createEnum(Provider, { enumName: "Provider" }),
  externalId: z.string(),
  teamId: z.string(),
  providerCreatedAt: z.date(),
  providerUpdatedAt: z.date(),
  updatedAt: z.date(),
  createdAt: z.date(),
  canonicalLocationExternalId: z.string().nullish(),
  shippingName: z.string().nullish(),
  supplierId: supplierIdFormat.schema.nullish(),
});
export const RedoFulfillmentLocationSchema: z.ZodType<RedoFulfillmentLocation> =
  RedoFulfillmentLocationSchema_;

export interface RedoFulfillmentLocation extends z.infer<
  typeof RedoFulfillmentLocationSchema_
> {}

export interface IRedoFulfillmentLocation extends z.ZodType<RedoFulfillmentLocation> {}

const CreateRedoFulfillmentLocationSchema_ =
  RedoFulfillmentLocationSchema_.omit({
    _id: true,
    updatedAt: true,
    createdAt: true,
  });
export const CreateRedoFulfillmentLocationSchema: z.ZodType<CreateRedoFulfillmentLocation> =
  CreateRedoFulfillmentLocationSchema_;

export interface CreateRedoFulfillmentLocation extends z.infer<
  typeof CreateRedoFulfillmentLocationSchema_
> {}

export function mapShopifyFulfillmentLocation(
  location: ShopifyFulfillmentLocation,
  teamId: string,
): CreateRedoFulfillmentLocation {
  const data = {
    ...location,
    provider: Provider.SHOPIFY,
    externalId: location.id,
    teamId,
    providerCreatedAt: new Date(location.createdAt),
    providerUpdatedAt: new Date(location.updatedAt),
    address: location.address
      ? {
          ...location.address,
          // Shopify often omits the full country name; derive it from countryCode
          // so the `country` column is never blank on new records.
          country:
            location.address.country ??
            (location.address.countryCode
              ? (findCountryName(location.address.countryCode) ?? null)
              : null),
        }
      : location.address,
  };
  return CreateRedoFulfillmentLocationSchema_.parse(data);
}
